import { isContractProcedure } from '@orpc/contract';
import {
  NOTIFICATION_ACTION_PAYLOAD_KEY,
  type NotificationActionPayload,
} from '@erp/shared-types';
import { contractRouter } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';

export type RpcCallable = (input: unknown) => Promise<unknown>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isActionPayload(value: unknown): value is NotificationActionPayload {
  if (!isRecord(value)) return false;
  return (
    typeof value['name'] === 'string' &&
    typeof value['endpoint'] === 'string' &&
    typeof value['actionPayload'] === 'string' &&
    (value['buttonType'] === 'primary' || value['buttonType'] === 'secondary')
  );
}

export function extractActions(
  payload: Record<string, unknown>,
): NotificationActionPayload[] {
  const raw = payload[NOTIFICATION_ACTION_PAYLOAD_KEY];
  if (!Array.isArray(raw)) return [];
  return raw.filter(isActionPayload);
}

/**
 * Whether `endpoint` names a real procedure in the RPC contract.
 *
 * This check cannot be made against `rpcClient`: every level of the oRPC client
 * is a `Proxy` over a function, so any path at all — including a typo — yields
 * something callable. The contract router is a plain nested object of contract
 * procedures, so it is the only place the path can actually be validated.
 */
function isContractEndpoint(parts: string[]): boolean {
  let cursor: unknown = contractRouter as unknown;
  for (const part of parts) {
    if (!isRecord(cursor)) return false;
    cursor = cursor[part];
  }
  return isContractProcedure(cursor);
}

export function resolveRpcCallable(endpoint: string): RpcCallable | null {
  const parts = endpoint.split('.');
  if (parts.some((part) => part === '')) return null;
  if (!isContractEndpoint(parts)) return null;

  // Walking the client proxy: each hop is a function, not a plain object, so
  // `isRecord` would reject it — the path is already validated above.
  let cursor: unknown = rpcClient as unknown;
  for (const part of parts) {
    if (cursor === null || cursor === undefined) return null;
    if (typeof cursor !== 'object' && typeof cursor !== 'function') return null;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return typeof cursor === 'function' ? (cursor as RpcCallable) : null;
}

/**
 * Result of parsing an action's JSON payload.
 *
 * A discriminated union rather than a sentinel value: the payload parses to
 * `unknown`, so any sentinel unioned with it is absorbed and the failure case
 * becomes untypeable.
 */
export type ParsedActionPayload =
  { readonly ok: true; readonly value: unknown } | { readonly ok: false };

export function parseActionPayload(raw: string): ParsedActionPayload {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false };
  }
}
